// components/Item-address/ItemAddress.jsx
import React from "react";
import { useSelector } from "react-redux";
import { BsStickyFill } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import {
  IoCheckmark,
  IoChevronForward,
  IoFolderOutline,
  IoRepeat,
} from "react-icons/io5";
import DropdownMenu from "../dropdown-menu/DropdownMenu";
import "./ItemAddress.scss";

const EMPTY = [];
const byName = (a, b) =>
  (a.name || "").localeCompare(b.name || "", undefined, {
    numeric: true,
    sensitivity: "base",
  });

// This is a controlled picker. The parent owns the IDs and all database writes.
export default function ItemAddress({
  type = "note",
  task,
  relatedHabit,
  spaceId = "",
  stationId = "",
  phaseId = "",
  setSpaceId,
  setStationId,
  setPhaseId,
  phases = EMPTY,
  phasesLoading = false,
  allowQuick = type === "note" || type === "task",
  showProject = type === "note" || type === "task",
  disabled = false,
  readOnly = false,
  className = "",
  style,
  dropdownProps = {},
  compactWorkspace = type === "task",
}) {
  const user = useSelector((state) => state.user.currentUser);
  const spaces = useSelector((state) => state.space.spaceData) || EMPTY;
  const stations = useSelector((state) => state.space.stationData) || EMPTY;
  const quickId =
    user?.uid && (type === "note" || type === "task")
      ? `${user.uid}-${type === "note" ? "QuickNotes" : "Inbox"}`
      : null;
  const quickName = type === "note" ? "Quick Notes" : "Quick Tasks";
  const isQuick = !!quickId && spaceId === quickId;
  const workspace = spaces.find((space) => space.spaceId === spaceId);
  const projects = stations
    .filter((project) => project.fromSpaceId === spaceId)
    .sort(byName);
  const project = projects.find((project) => project.stationId === stationId);
  const phase = phases.find((item) => String(item.id) === String(phaseId));
  const locked = disabled || readOnly || !user?.uid;

  const navigate = useNavigate();

  const isHabitReminder = type === "task" && task?.type === "habitReminder";

  const workspaceName = isQuick
    ? quickName
    : workspace?.name ||
      (spaceId ? "Workspace unavailable" : "Choose workspace");
  const projectName =
    project?.name || (stationId ? "Project unavailable" : "Choose project");
  const phaseName =
    phase?.name || (phaseId ? "Phase unavailable" : "Choose phase");

  const openHabit = () => {
    if (!relatedHabit?.id) return;

    navigate({
      pathname: "/",
      search: `?habit=${encodeURIComponent(relatedHabit.id)}`,
    });
  };

  const workspaceIcon = (space, quick = false) => (
    <span
      className="itemAddress_badge"
      style={{ backgroundColor: quick ? undefined : space?.color }}
    >
      {quick ? (
        type === "note" ? (
          <BsStickyFill />
        ) : (
          <IoCheckmark />
        )
      ) : (
        space?.name?.charAt(0) || "?"
      )}
    </span>
  );
  const projectIcon = (project) =>
    project?.emojicon ? (
      <span className="itemAddress_emoji">{project.emojicon}</span>
    ) : (
      <IoFolderOutline />
    );

  const workspaceItems = [
    ...(allowQuick && quickId
      ? [{ key: quickId, label: quickName, icon: workspaceIcon(null, true) }]
      : []),
    ...[...spaces].sort(byName).map((space) => ({
      key: space.spaceId,
      label: space.name || "Unnamed workspace",
      icon: workspaceIcon(space),
    })),
  ];

  const chooseWorkspace = (nextId) => {
    if (nextId === spaceId) return;
    const firstProject =
      showProject && nextId !== quickId
        ? stations
            .filter((project) => project.fromSpaceId === nextId)
            .sort(byName)[0]
        : null;
    setSpaceId(nextId);
    setStationId?.(firstProject?.stationId || "");
    setPhaseId?.("");
  };
  const chooseProject = (nextId) => {
    if (nextId === stationId) return;
    setStationId(nextId);
    setPhaseId?.("");
  };

  const menuProps = {
    align: "start",
    minWidth: 220,
    maxWidth: 320,
    itemHeight: 36,
    ...dropdownProps,
    className: `itemAddress_segment ${dropdownProps.className || ""}`,
    triggerClassName: `itemAddress_trigger ${dropdownProps.triggerClassName || ""}`,
    menuClassName: `itemAddress_menu ${dropdownProps.menuClassName || ""}`,
    style: {
      "--dm-font-size": "13px",
      "--dm-icon-size": "24px",
      ...dropdownProps.style,
    },
  };

  // With no setter (or readOnly), show a normal breadcrumb rather than a dead button.
  const renderSegment = ({
    kind,
    label,
    icon,
    items,
    value,
    setter,
    onSelect,
    emptyLabel,
  }) => {
    const content = (
      <>
        {icon && (
          <span className="itemAddress_icon" aria-hidden="true">
            {icon}
          </span>
        )}
        {!(kind === "workspace" && compactWorkspace && value) && (
          <span className="itemAddress_label">{label}</span>
        )}
      </>
    );
    return readOnly || !setter ? (
      <span
        className={`itemAddress_static ${kind === "workspace" && compactWorkspace && value ? "itemAddress_compact" : ""}`}
        title={label}
      >
        {content}
      </span>
    ) : (
      <DropdownMenu
        {...menuProps}
        className={`${menuProps.className} ${kind === "workspace" && compactWorkspace && value ? "itemAddress_compact" : ""}`}
        hasCaret={
          kind === "workspace" && compactWorkspace && value
            ? false
            : menuProps.hasCaret
        }
        key={`${kind}:${kind === "workspace" ? "" : spaceId}:${kind === "phase" ? stationId : ""}`}
        triggerLabel={`${kind}: ${label}`}
        customBtn={content}
        buttonTitle={label}
        items={items}
        selectedKey={value || undefined}
        onSelect={onSelect}
        disabled={locked || (kind === "phase" && phasesLoading)}
        emptyLabel={emptyLabel}
        triggerClassName={`${menuProps.triggerClassName} ${!value ? "is-placeholder" : ""}`}
      />
    );
  };

  if (isHabitReminder) {
    return (
      <div
        className={`itemAddress_mainRow ${className}`}
        style={style}
        role="group"
        aria-label="Habit reminder location"
      >
        <span className="itemAddress_static itemAddress_compact" title="Habits">
          <span className="itemAddress_icon" aria-hidden="true">
            <span className="itemAddress_badge">
              <IoRepeat />
            </span>
          </span>
        </span>

        <IoChevronForward
          className="itemAddress_separator"
          aria-hidden="true"
        />

        <span className="itemAddress_static">
          <span className="itemAddress_label">Habits</span>
        </span>

        <IoChevronForward
          className="itemAddress_separator"
          aria-hidden="true"
        />

        <button
          type="button"
          className="itemAddress_static itemAddress_habitLink"
          onClick={openHabit}
          disabled={!relatedHabit}
          title={relatedHabit?.name || "Habit unavailable"}
        >
          <span className="itemAddress_emoji" aria-hidden="true">
            {relatedHabit?.icon || "🔁"}
          </span>

          <span className="itemAddress_label">
            {relatedHabit?.name || "Habit unavailable"}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      className={`itemAddress_mainRow ${className}`}
      style={style}
      role="group"
      aria-label="Item location"
    >
      {renderSegment({
        kind: "workspace",
        label: workspaceName,
        icon: workspaceIcon(workspace, isQuick),
        items: workspaceItems,
        value: spaceId,
        setter: setSpaceId,
        onSelect: chooseWorkspace,
        emptyLabel: "No workspaces available",
      })}

      {showProject && spaceId && !isQuick && (
        <>
          <IoChevronForward
            className="itemAddress_separator"
            aria-hidden="true"
          />
          {renderSegment({
            kind: "project",
            label: projectName,
            icon: projectIcon(project),
            items: projects.map((item) => ({
              key: item.stationId,
              label: item.name || "Unnamed project",
              icon: projectIcon(item),
            })),
            value: stationId,
            setter: setStationId,
            onSelect: chooseProject,
            emptyLabel: "No projects in this workspace",
          })}
        </>
      )}

      {type === "task" &&
        showProject &&
        !isQuick &&
        project &&
        (setPhaseId || phaseId) && (
          <>
            <IoChevronForward
              className="itemAddress_separator"
              aria-hidden="true"
            />
            {renderSegment({
              kind: "phase",
              label: phasesLoading ? "Loading phases…" : phaseName,
              items: phases.map((item) => ({
                key: item.id,
                label: item.name || "Unnamed phase",
              })),
              value: phaseId,
              setter: setPhaseId,
              onSelect: setPhaseId,
              emptyLabel: "No phases available",
            })}
          </>
        )}
    </div>
  );
}
